// Visão Geral (rota `/`) por perfil — #6493, projeto 24.
//
// Escolhe a tela pelo perfil de quem está logado (src/lib/visaoGeral/perfis.ts).
// Administrador pode "ver como" outra pessoa: a tela e os números passam a ser os
// dela. Perfil ainda sem tela própria cai na Visão Geral antiga (DashboardHome).
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, Hammer } from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { api } from '../../src/services/api';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import DashboardHome from '../DashboardHome';
import { DEFINICOES_PERFIL, PERFIS_VISAO_GERAL, resolverPerfil, type PerfilVisaoGeral } from '../../src/lib/visaoGeral/perfis';
import { mesDe, periodoDoDia, periodoDoMes } from '../../src/lib/visaoGeral/periodo';
import type { Profile } from '../../types';
import { TELAS } from './telas';
import { BlocosDoDia } from './BlocosDoDia';

interface VerComoProps {
  pessoas: Profile[] | null;
  meuPerfil: PerfilVisaoGeral;
  verId: string | null;
  aoMudar: (id: string | null) => void;
}

/** Seletor "Ver como", só para administradores. Agrupa as pessoas pela tela que elas veem. */
const VerComo: React.FC<VerComoProps> = ({ pessoas, meuPerfil, verId, aoMudar }) => {
  const grupos = useMemo(() => {
    const porPerfil = new Map<PerfilVisaoGeral, Profile[]>();
    for (const p of pessoas || []) {
      const perfil = resolverPerfil(p);
      if (!porPerfil.has(perfil)) porPerfil.set(perfil, []);
      porPerfil.get(perfil)!.push(p);
    }
    return PERFIS_VISAO_GERAL
      .filter(id => porPerfil.has(id))
      .map(id => ({ id, pessoas: porPerfil.get(id)!.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')) }));
  }, [pessoas]);

  return (
    <div className="flex justify-end">
      <div className="inline-flex flex-wrap items-center gap-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 shadow-sm max-w-full">
        <Eye size={15} className="text-gray-400 shrink-0" />
        <label htmlFor="vg-ver" className="text-gray-500 dark:text-gray-400 font-medium">Ver como</label>
        <select
          id="vg-ver"
          value={verId ?? ''}
          onChange={(e) => aoMudar(e.target.value || null)}
          disabled={pessoas === null}
          className="min-w-0 max-w-[16rem] bg-transparent text-gray-900 dark:text-white font-semibold outline-none cursor-pointer dark:[color-scheme:dark]"
        >
          <option value="">Eu ({DEFINICOES_PERFIL[meuPerfil].rotulo})</option>
          {grupos.map(g => (
            <optgroup key={g.id} label={`${DEFINICOES_PERFIL[g.id].rotulo}${TELAS[g.id] ? '' : ' · em construção'}`}>
              {g.pessoas.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </optgroup>
          ))}
        </select>
        {verId && (
          <button type="button" onClick={() => aoMudar(null)} className="text-xs font-semibold text-primary hover:underline">
            Voltar para a minha
          </button>
        )}
      </div>
    </div>
  );
};

const VisaoGeralView: React.FC = () => {
  const { profile, loading } = useAuth();
  const [params, setParams] = useSearchParams();
  const ehAdm = profile?.role === 'adm';
  const verId = ehAdm ? params.get('ver') : null;
  const [pessoas, setPessoas] = useState<Profile[] | null>(null);

  useEffect(() => {
    if (!ehAdm) return;
    let vivo = true;
    api.auth.listProfiles()
      .then(lista => { if (vivo) setPessoas(lista.filter(p => p.status !== 'inactive')); })
      .catch(() => { if (vivo) setPessoas([]); });
    return () => { vivo = false; };
  }, [ehAdm]);

  if (!profile) {
    // O AuthContext destrava a tela em 8s mesmo sem perfil carregado: aí fica a Visão Geral antiga.
    return loading ? <LoadingSpinner label="Carregando..." /> : <DashboardHome />;
  }
  if (verId && pessoas === null) return <LoadingSpinner label="Carregando..." />;

  const outra = verId ? pessoas?.find(p => p.id === verId) ?? null : null;
  const pessoa = outra ?? profile;
  const perfil = resolverPerfil(pessoa);
  const definicao = DEFINICOES_PERFIL[perfil];
  const Tela = TELAS[perfil];
  const hoje = new Date();
  const periodo = definicao.periodo === 'mes' ? periodoDoMes(params.get('mes'), hoje) : periodoDoDia(hoje);

  const mudarParametro = (chave: string, valor: string | null) => {
    const novos = new URLSearchParams(params);
    if (valor) novos.set(chave, valor); else novos.delete(chave);
    setParams(novos, { replace: true });
  };

  return (
    <div className="space-y-6 w-full mx-auto animate-fade-in">
      {ehAdm && <VerComo pessoas={pessoas} meuPerfil={resolverPerfil(profile)} verId={outra?.id ?? null} aoMudar={(id) => mudarParametro('ver', id)} />}
      {Tela ? (
        <Tela
          key={pessoa.id}
          pessoa={pessoa}
          souEu={!outra}
          definicao={definicao}
          periodo={periodo}
          aoMudarMes={definicao.periodo === 'mes' ? (mes) => mudarParametro('mes', mes === mesDe(hoje) ? null : mes) : undefined}
        />
      ) : (
        <>
          {outra && (
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
              <Hammer size={16} className="shrink-0 mt-0.5" />
              <p>A tela de <b>{definicao.rotulo}</b>, que {outra.full_name} vai ver, ainda está sendo construída (projeto 24). Os avisos e a caixa de entrada abaixo já são os dessa pessoa; o resto é a Visão Geral atual, com os seus números.</p>
            </div>
          )}
          {/* Avisos e caixa de entrada já valem antes da tela do perfil ficar pronta (#6494). */}
          <BlocosDoDia key={pessoa.id} pessoa={pessoa} perfil={perfil} />
          <DashboardHome />
        </>
      )}
    </div>
  );
};

export default VisaoGeralView;
